// Состояние установки PWA. Chrome/Edge/Яндекс на Android и десктопе присылают
// beforeinstallprompt один раз и рано — ещё до того, как человек дойдёт до
// настроек. Событие ловит PwaRegister (корневой layout) и кладёт сюда, а блок
// «Приложение на телефоне» забирает его, когда смонтируется.

export type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
const listeners = new Set<() => void>()

export function setInstallPrompt(e: InstallPromptEvent | null) {
  deferred = e
  listeners.forEach((fn) => fn())
}

export function getInstallPrompt() {
  return deferred
}

export function onInstallPromptChange(fn: () => void) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

// Кнопку "Установить приложение" (дашборд, лендинг) убрали крестиком или
// "Я установил" на iPhone: Safari не узнаёт, что сайт уже на экране "Домой",
// поэтому помним это сами, на этом устройстве.
const DISMISS_KEY = 'tk-install-offer-dismissed'

export function isInstallOfferDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export function dismissInstallOffer() {
  try {
    localStorage.setItem(DISMISS_KEY, '1')
  } catch {}
  listeners.forEach((fn) => fn())
}

// Запущено как установленное приложение (с экрана «Домой»), а не во вкладке.
export function isStandalone() {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

// iPhone/iPad: beforeinstallprompt нет, ставят вручную через «Поделиться».
// iPadOS 13+ представляется Mac'ом — отличаем по сенсорному экрану.
export function isIos() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

// Android без beforeinstallprompt: где в меню браузера пункт установки.
// Яндекс Браузер события не присылает, а меню у него с тремя полосками внизу
// и пункт называется "Добавить ярлык" (обращение 145, 29.10). Samsung Internet
// тоже прячет его в нижнее меню. Samsung и Xiaomi без разрешения "Ярлыки на
// рабочем столе" молча не создают значок, поэтому про него пишем всем.
export function androidInstallSteps(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const perm =
    ' Если значок не появился, разрешите браузеру создавать ярлыки: настройки телефона, Приложения, браузер, Разрешения.'
  if (/YaBrowser|YaApp/.test(ua))
    return 'Нажмите кнопку с тремя полосками внизу справа, пролистайте меню вниз и выберите "Добавить ярлык", потом "Добавить".' + perm
  if (/SamsungBrowser/.test(ua))
    return 'Нажмите кнопку с тремя полосками внизу справа, выберите "Добавить страницу на", потом "Главный экран".' + perm
  return 'Откройте меню браузера (три точки вверху справа) и выберите "Установить приложение" или "Добавить на главный экран".' + perm
}
